import { Injectable } from '@angular/core';
import {
  DprConsumableDetailGridData,
  DprPersonnelDetailGridData,
} from '@dpr-details/models/grid';
import {
  DailyStockDetails,
  DprConsumableDetail,
  DprPersonnelDetail,
} from '@shared/models/dpr';

@Injectable()
export class DailyStockService {
  buildDailyStockDetails(
    data: DprConsumableDetailGridData | DprPersonnelDetailGridData
  ): DailyStockDetails {
    if (!data) {
      return null;
    }

    return {
      totalStartOfDay: data.dailyStockDetailsTotalStartOfDay,
      totalEndOfDay: data.dailyStockDetailsTotalEndOfDay,
      totalUsed: data.dailyStockDetailsTotalUsed,
    };
  }

  onAddedChanged(
    dailyStockAction: DprConsumableDetailGridData | DprPersonnelDetailGridData,
    dailyStockDetails: DailyStockDetails
  ): DailyStockDetails {
    dailyStockDetails = dailyStockDetails ?? {};

    if (!dailyStockAction) {
      return dailyStockDetails;
    }

    dailyStockDetails.totalEndOfDay = this.calculateTotalEndOfDay(
      dailyStockDetails.totalStartOfDay,
      dailyStockAction.added,
      dailyStockAction.used
    );

    return dailyStockDetails;
  }

  onUsedChanged(
    dailyStockAction: DprConsumableDetailGridData | DprPersonnelDetailGridData,
    dailyStockDetails: DailyStockDetails,
    oldValue: number
  ): DailyStockDetails {
    dailyStockDetails = dailyStockDetails ?? {};

    if (!dailyStockDetails) {
      return dailyStockDetails;
    }

    dailyStockDetails.totalEndOfDay = this.calculateTotalEndOfDay(
      dailyStockDetails.totalStartOfDay,
      dailyStockAction.added,
      dailyStockAction.used
    );

    const totalUsedBeforeToday =
      (dailyStockDetails.totalUsed ?? 0) - (oldValue ?? 0);

    dailyStockDetails.totalUsed = this.calculateTotalUsed(
      totalUsedBeforeToday,
      dailyStockAction.used
    );

    return dailyStockDetails;
  }

  /**
   * @description Updates stock details for a day based on changes from the previous day
   */
  onPreviousDayChanged(
    previousDayDailyStockDetails: DailyStockDetails,
    dailyStockAction?: DprConsumableDetail | DprPersonnelDetail,
    dailyStockDetails?: DailyStockDetails
  ): DailyStockDetails {
    dailyStockDetails = { ...(dailyStockDetails ?? {}) };
    previousDayDailyStockDetails = previousDayDailyStockDetails ?? {};

    dailyStockDetails.totalStartOfDay =
      previousDayDailyStockDetails.totalEndOfDay ?? 0;
    dailyStockDetails.totalEndOfDay = this.calculateTotalEndOfDay(
      dailyStockDetails.totalStartOfDay,
      dailyStockAction?.added,
      dailyStockAction?.used
    );
    dailyStockDetails.totalUsed = this.calculateTotalUsed(
      previousDayDailyStockDetails.totalUsed,
      dailyStockAction?.used
    );

    return dailyStockDetails;
  }

  /**
   * @description Determines if the stock should be auto-filled
   */
  shouldTriggerAutoFillOfStock(
    data: DprConsumableDetailGridData[] | DprPersonnelDetailGridData[]
  ): boolean {
    if (!data) {
      return false;
    }

    const someItemsFilled = data.some(
      (reg) => reg.added != null || reg.used != null
    );
    const someItemsEmpty = data.some(
      (reg) => reg.added == null || reg.used == null
    );

    return someItemsFilled && someItemsEmpty;
  }

  isDailyStockActionCompleted(
    dailyStockAction: DprConsumableDetail | DprPersonnelDetail
  ): boolean {
    if (!dailyStockAction) {
      return false;
    }

    return dailyStockAction.added != null || dailyStockAction.used != null;
  }

  isDailyClassicActionCompleted(
    dailyClassicAction: DprPersonnelDetail
  ): boolean {
    if (!dailyClassicAction) {
      return false;
    }

    return dailyClassicAction.currentDay != null;
  }

  hasTotalEndOfDay(dailyStockDetails: DailyStockDetails): boolean {
    if (!dailyStockDetails) {
      return false;
    }

    return dailyStockDetails?.totalEndOfDay > 0;
  }

  //#region Private methods

  private calculateTotalEndOfDay(
    totalStartOfDay: number,
    added: number,
    used: number
  ): number {
    return (totalStartOfDay ?? 0) + (added ?? 0) - (used ?? 0);
  }

  private calculateTotalUsed(
    totalUsedBeforeToday: number,
    usedToday: number
  ): number {
    return (totalUsedBeforeToday ?? 0) + (usedToday ?? 0);
  }

  //#endregion
}
